import type { TaxEntity } from "../../domain/entities/TaxEntity";
import type { ChangeDetails } from "../../utils/reviewWorkflow";
import type { InvoiceBillLineLike } from "./invoiceBillDisplayValues";
import { buildInvoiceBillDisplayDiff } from "./buildInvoiceBillDisplayDiff";
import {
  INVOICE_HEADER_FIELDS,
  INVOICE_LINE_FIELDS,
} from "./invoiceBillAllowlists";

/**
 * Matches `formatFieldName` in InvoiceReviewPage for change tracking labels.
 * Keep in sync when editing tracking field names.
 */
export function formatInvoiceTrackingFieldName(fieldName: string): string {
  const formatted = fieldName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  if (
    formatted.toLowerCase() === "gst no" ||
    formatted.toLowerCase() === "gstno"
  ) {
    return "Vendor GSTIN";
  }

  if (
    formatted.toLowerCase() === "customer gstin" ||
    formatted.toLowerCase() === "customer_gstin"
  ) {
    return "Customer GSTIN";
  }

  return formatted;
}

function headerFieldLabelCandidates(canonicalKey: string): string[] {
  const row = INVOICE_HEADER_FIELDS.find((f) => f.key === canonicalKey);
  const set = new Set<string>();
  if (row) set.add(row.label);
  set.add(formatInvoiceTrackingFieldName(canonicalKey));
  if (canonicalKey === "customer_name") {
    set.add("Customer");
  }
  return Array.from(set);
}

function resolveInvoiceHeaderKeyFromChangeField(
  field: string,
): string | undefined {
  for (const { key } of INVOICE_HEADER_FIELDS) {
    if (headerFieldLabelCandidates(key).includes(field)) {
      return key;
    }
  }
  return undefined;
}

function resolveInvoiceLineKeyFromSubfieldLabel(
  subfieldLabel: string,
): string | undefined {
  for (const { key, label } of INVOICE_LINE_FIELDS) {
    if (subfieldLabel === label) return key;
    if (subfieldLabel === formatInvoiceTrackingFieldName(key)) return key;
  }
  return undefined;
}

const LINE_ITEM_FIELD_RE = /^Line Item (\d+): (.+)$/;
const LINE_ITEM_REMOVED_RE = /^Line Item (\d+)$/;

/** Whether a persisted `field_change.field` string is allowlisted for invoice review display / tracking. */
export function isInvoiceTrackedFieldChangeRowAllowed(field: string): boolean {
  if (!field) return false;
  if (field === "customer_id") return false;
  if (field === "Customer") return true;
  if (LINE_ITEM_REMOVED_RE.test(field)) return true;
  const lineMatch = LINE_ITEM_FIELD_RE.exec(field);
  if (lineMatch) {
    return resolveInvoiceLineKeyFromSubfieldLabel(lineMatch[2]) !== undefined;
  }
  return resolveInvoiceHeaderKeyFromChangeField(field) !== undefined;
}

const SNAPSHOT_LINE_TOTAL_KEYS = new Set([
  "item_total",
  "item_total_inclusive_of_tax",
]);

export interface InvoicePendingReviewTextDiff {
  oldText: string;
  newText: string;
}

/**
 * Strike-through maps for a **pending** invoice review, driven by persisted
 * `change_details` (not live editor JSON).
 *
 * - Header + most line cells: `field_changes` from the review payload (same strings
 *   the submit flow recorded).
 * - Line amount columns: `item_total` / `item_total_inclusive_of_tax` are not written
 *   to `field_changes`; those come from `original_json` vs `raw_json` snapshot diff
 *   when both are present on `change_details`.
 */
export function buildInvoicePendingReviewDisplayMapsFromChangeDetails(
  changeDetails: ChangeDetails | null | undefined,
  taxes: TaxEntity[] | undefined,
): {
  header: Map<string, InvoicePendingReviewTextDiff>;
  line: Map<string, InvoicePendingReviewTextDiff>;
} {
  const header = new Map<string, InvoicePendingReviewTextDiff>();
  const line = new Map<string, InvoicePendingReviewTextDiff>();

  if (!changeDetails) {
    return { header, line };
  }

  const fieldChanges = changeDetails.field_changes;
  if (fieldChanges?.length) {
    for (const fc of fieldChanges) {
      const field = fc.field;
      if (!field || field === "customer_id") continue;

      const lineMatch = LINE_ITEM_FIELD_RE.exec(field);
      if (lineMatch) {
        const lineNum = Number(lineMatch[1]);
        const sub = lineMatch[2];
        const lineKey = resolveInvoiceLineKeyFromSubfieldLabel(sub);
        if (lineKey) {
          line.set(`${lineNum}-${lineKey}`, {
            oldText: fc.old,
            newText: fc.new,
          });
        }
        continue;
      }

      if (LINE_ITEM_REMOVED_RE.test(field)) {
        continue;
      }

      const headerKey = resolveInvoiceHeaderKeyFromChangeField(field);
      if (headerKey) {
        header.set(headerKey, { oldText: fc.old, newText: fc.new });
      }
    }
  }

  if (changeDetails.original_json != null && changeDetails.raw_json != null) {
    const vs = changeDetails.validation_snapshot;
    const validationSnapshot =
      vs && Array.isArray(vs.line_items) && vs.line_items.length > 0
        ? {
            line_items: vs.line_items as
              | InvoiceBillLineLike[]
              | null
              | undefined,
          }
        : undefined;

    const items = buildInvoiceBillDisplayDiff({
      kind: "invoice",
      originalHeader: changeDetails.original_json,
      newHeader: changeDetails.raw_json,
      originalLineItems: changeDetails.original_json?.line_items,
      newLineItems: changeDetails.raw_json?.line_items,
      taxes,
      validationSnapshot: validationSnapshot ?? null,
    });
    for (const d of items) {
      if (
        d.scope !== "line" ||
        d.lineNumber == null ||
        !SNAPSHOT_LINE_TOTAL_KEYS.has(d.fieldKey)
      ) {
        continue;
      }
      const k = `${d.lineNumber}-${d.fieldKey}`;
      if (!line.has(k)) {
        line.set(k, { oldText: d.oldText, newText: d.newText });
      }
    }
  }

  return { header, line };
}
